import { getCookie } from './cookie';
import { trackEvent } from './omega';
import url from './url';

import { TechPerformanceId, TechPerformanceErrorType } from '@/constants/pub_event';

/**
 * 性能追踪统一管理工具
 * 封装 window.performanceTracker 相关方法，统一管理性能埋点
 */

interface MarkPopParams {
    popup_id: string;
    page_id?: string;
    page_version?: number | string;
}

interface InitTaskTrackerConfig {
    requiredTasks: string[];
    exec?: () => void;
}

const getErrorMsg = (error: unknown) => {
    let msg = '';

    try {
        msg = JSON.stringify(error);
    } catch {
        msg = String(error);
    }

    return msg;
};

/**
 * 上报 P3 性能指标
 */
export const reportP3 = (): void => {
    try {
        if (window.performanceTracker) {
            window.performanceTracker.reportP3();
        } else {
            trackEvent(TechPerformanceId.TECH_PERFORMANCE_ERROR_TYPE, {
                error_type: TechPerformanceErrorType.p3_not_init,
            });
        }
    } catch (error) {
        // 避免埋点异常影响主流程
        console.error('reportP3 error:', error);
        trackEvent(TechPerformanceId.TECH_PERFORMANCE_ERROR_TYPE, {
            error_type: TechPerformanceErrorType.p3_catch_error,
            error_message: getErrorMsg(error),
        });
    }
};

/**
 * 上报 P4 性能指标（直接上报）
 */
export const reportP4Direct = (): void => {
    try {
        if (window.performanceTracker) {
            window.performanceTracker.reportP4Direct();
        } else {
            trackEvent(TechPerformanceId.TECH_PERFORMANCE_ERROR_TYPE, {
                error_type: TechPerformanceErrorType.p4_direct_not_init,
            });
        }
    } catch (error) {
        console.error('reportP4Direct error:', error);
        trackEvent(TechPerformanceId.TECH_PERFORMANCE_ERROR_TYPE, {
            error_type: TechPerformanceErrorType.p4_direct_catch_error,
            error_message: getErrorMsg(error),
        });

    }
};

/**
 * 前置更新uid
 */
export const updateUid = (): void => {
    try {
        const uid = getCookie('uid') || url.get('uid') || '';

        updateCommonParams({ uid, pub_uid: uid });
    } catch (error) {
        console.error('updateuid error:', error);
    }
};

/**
 * 标记弹窗开始
 * @param params 弹窗参数
 */
export const markPopBegin = (params: MarkPopParams): void => {
    try {
        if (window.performanceTracker) {
            window.performanceTracker.markPopBegin({
                popup_id: params.popup_id,
            });
        }
    } catch (error) {
        console.error('markPopBegin error:', error);
    }
};

/**
 * 标记弹窗结束
 * @param params 弹窗参数
 */
export const markPopEnd = (params: MarkPopParams): void => {
    try {
        if (window.performanceTracker) {
            window.performanceTracker.markPopEnd({
                popup_id: params.popup_id,
            });
        }
    } catch (error) {
        console.error('markPopEnd error:', error);
    }
};

/**
 * 初始化任务追踪器
 * @param config 任务追踪器配置
 * @returns 任务追踪器实例
 */
export const initTaskTracker = (config: InitTaskTrackerConfig) => {
    try {
        if (window.performanceTracker) {
            window.taskPerformanceTasker = window.performanceTracker.taskTrackerTool.initTaskTracker({
                requiredTasks: config.requiredTasks,
                exec: () => {
                    reportP4Direct();
                    if (config.exec) {
                        config.exec();
                    }
                }
            });

            return window.taskPerformanceTasker;
        }

        return null;
    } catch (error) {
        console.error('initTaskTracker error:', error);

        return null;
    }
};

/**
 * 标记任务完成
 * @param taskName 任务名称
 */
export const markTaskCompleted = (taskName: string): void => {
    try {
        if (window.taskPerformanceTasker) {
            window.taskPerformanceTasker.markTaskCompleted(taskName);
        }
    } catch (error) {
        console.error('markTaskCompleted error:', error);
    }
};

/**
 * 更新 CommonParams，合并传入的 params 到当前公共参数
 * 业务侧可在登录后等场景调用，用于将 uid 等写入性能埋点公共参数
 * （UMD 下可能与 core/media 一致挂载在 window.performance 上，由打包统一挂载）
 */
export const updateCommonParams = (params: Record<string, unknown>): void => {
    try {
        if (window.performanceTracker?.updateCommonParams) {
            window.performanceTracker.updateCommonParams(params);
        }
    } catch (error) {
        console.error('updateCommonParams error:', error);
    }
};

/**
 * 上报页面停留时长
 */
export const pageLeave = (): void => {
    try {
        if (window.performanceTracker) {
            window.performanceTracker.pageLeave();
        }
    } catch (error) {
        console.error('pageLeave error:', error);
    }
};
